/**
 * Design Form Component
 * Modal form for uploading and managing work designs
 * Supports BIM files, CAD files, PDFs, and supporting documents
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { designsApi, worksApi, proposalsApi, estimatesApi } from '../../services/wmsService';
import { useToast } from '@/hooks/useToast';
// DesignFile is an interface (re-exported from DigitalTwin/types via `export *`), so it
// needs the `type` marker — esbuild would otherwise emit a runtime import for it.
import { DigitalTwinViewer, type DesignFile as ViewerDesignFile } from '@/components/DigitalTwin';
import WorkSelector, { type SelectedWork } from '../WorkSelector';
import { ListSkeleton, TableSkeleton } from '@/components/base/Skeleton';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_ICON, WMS_POPUP_CLOSE } from '../common/wmsPopupHeader';

// Types
interface DesignFile {
  id: string;
  file: File | null;
  name: string;
  type: 'BIM' | 'CAD' | 'GIS' | 'PDF' | 'IMAGE' | 'OTHER';
  size: number;
  url?: string;
  uploaded: boolean;
  uploading: boolean;
  error?: string;
  description: string;
  drawingId?: number;  // Database ID for uploaded files
  incl_dwtn_in?: boolean;  // Include in Digital Twin viewer
}

interface Work {
  workId: number;
  workCode: string;
  workName: string;
  workTypeName?: string;
  schemeName?: string;
  zoneName?: string;
  wardName?: string;
  originalEstimate?: number;
}

interface DesignFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  workId?: number | null;
  designId?: number | null;
}

// File type configuration
const FILE_TYPES = {
  BIM: {
    label: 'BIM Files',
    icon: 'ri-building-3-line',
    color: 'blue',
    extensions: ['.ifc', '.rvt', '.nwd', '.nwc', '.nwf', '.dgn'],
    accept: '.ifc,.rvt,.nwd,.nwc,.nwf,.dgn'
  },
  CAD: {
    label: 'CAD Files',
    icon: 'ri-pencil-ruler-2-line',
    color: 'purple',
    extensions: ['.dwg', '.dxf', '.dwf', '.dws'],
    accept: '.dwg,.dxf,.dwf,.dws'
  },
  GIS: {
    label: 'GIS Files',
    icon: 'ri-map-2-line',
    color: 'teal',
    extensions: ['.kml', '.kmz', '.shp', '.shx', '.dbf', '.prj', '.geojson', '.gpx'],
    accept: '.kml,.kmz,.shp,.shx,.dbf,.prj,.geojson,.gpx'
  },
  PDF: {
    label: 'PDF Documents',
    icon: 'ri-file-pdf-line',
    color: 'red',
    extensions: ['.pdf'],
    accept: '.pdf'
  },
  IMAGE: {
    label: 'Images',
    icon: 'ri-image-line',
    color: 'green',
    extensions: ['.jpg', '.jpeg', '.png', '.gif', '.tiff', '.bmp'],
    accept: '.jpg,.jpeg,.png,.gif,.tiff,.bmp'
  },
  OTHER: {
    label: 'Other Documents',
    icon: 'ri-file-line',
    color: 'gray',
    extensions: ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.zip', '.rar'],
    accept: '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar'
  }
};

const ALL_ACCEPTED_EXTENSIONS = Object.values(FILE_TYPES).map(t => t.accept).join(',');

// Design stages
const DESIGN_STAGES = [
  { code: 'PROPOSED', name: 'Proposed', color: 'gray' },
  { code: 'UNDER_DESIGN', name: 'Under Design', color: 'blue' },
  { code: 'SUBMITTED', name: 'Submitted', color: 'amber' },
  { code: 'UNDER_REVIEW', name: 'Under Review', color: 'orange' },
  { code: 'APPROVED', name: 'Approved', color: 'green' },
  { code: 'GROUNDED', name: 'Grounded', color: 'emerald' }
];

// Helper to determine file type from extension
function getFileType(filename: string): DesignFile['type'] {
  const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  for (const [type, config] of Object.entries(FILE_TYPES)) {
    if (config.extensions.includes(ext)) {
      return type as DesignFile['type'];
    }
  }
  return 'OTHER';
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Section Header Component
function SectionHeader({ icon, title, action }: { icon: string; title: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-3 px-3 py-2 bg-gradient-to-r from-indigo-600 to-purple-500 rounded-lg">
      <div className="flex items-center gap-2">
        <i className={`${icon} text-white`}></i>
        <h4 className="font-medium text-white">{title}</h4>
      </div>
      {action}
    </div>
  );
}

export default function DesignForm({ isOpen, onClose, onSuccess, workId, designId }: DesignFormProps) {
  const { success: showSuccess, error: showError } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Work data
  const [work, setWork] = useState<Work | null>(null);
  const [works, setWorks] = useState<Work[]>([]);
  const [selectedWorkId, setSelectedWorkId] = useState<number | null>(workId || null);
  const [workSearchTerm, setWorkSearchTerm] = useState('');
  const [showWorkPicker, setShowWorkPicker] = useState(false);

  // Existing designs already linked to the picked work (used to prevent duplicates)
  interface ExistingDesign {
    dsgn_id: number; dsgn_cd: string; dsgn_nm: string; dsgn_stge_cd: string;
    dsgn_stge_nm?: string; dsgn_sts_cd?: string; estmt_val_at?: number; vrsn_nu?: number;
  }
  const [existingDesigns, setExistingDesigns] = useState<ExistingDesign[]>([]);

  // Attached proposals (M:M with the work)
  interface AttachedProposal {
    prpsl_id: number; prpsl_cd: string; prpsl_nm: string; estmt_cst_at: number;
    prpsl_sts_cd: string; prpsl_dt: string | null; lctn_tx: string | null;
  }
  const [attachedProposals, setAttachedProposals] = useState<AttachedProposal[]>([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [showProposalPicker, setShowProposalPicker] = useState(false);
  const [proposalPickerSearch, setProposalPickerSearch] = useState('');
  const [proposalPickerResults, setProposalPickerResults] = useState<AttachedProposal[]>([]);
  const [proposalPickerLoading, setProposalPickerLoading] = useState(false);
  const [proposalPickerSelected, setProposalPickerSelected] = useState<Set<number>>(new Set());
  const proposalPickerSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Form data
  const [designCode, setDesignCode] = useState('');
  const [designName, setDesignName] = useState('');
  const [designStage, setDesignStage] = useState<string>('PROPOSED');
  const [designStatus, setDesignStatus] = useState<string>('DRAFT');
  const [estimateValue, setEstimateValue] = useState<number>(0);
  const [version, setVersion] = useState<number>(1);
  const [remarks, setRemarks] = useState('');
  const [submittedDate, setSubmittedDate] = useState<string>('');
  const [approvedDate, setApprovedDate] = useState<string>('');

  // Files
  const [files, setFiles] = useState<DesignFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  // Viewer
  const [showViewer, setShowViewer] = useState(false);
  const [viewerFiles, setViewerFiles] = useState<ViewerDesignFile[]>([]);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; fileId: string | null; fileName: string }>({
    show: false,
    fileId: null,
    fileName: ''
  });

  // Save status message (displayed in modal)
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: ''
  });

  // Track if a successful save happened (to refresh parent on close)
  const [hasSavedSuccessfully, setHasSavedSuccessfully] = useState(false);

  const isEdit = !!designId;

  // Handle modal close - refresh parent if save was successful
  const handleClose = () => {
    if (hasSavedSuccessfully) {
      onSuccess();
    }
    onClose();
  };

  // Load works for picker
  useEffect(() => {
    const loadWorks = async () => {
      try {
        const response = await worksApi.getAll({}, 1, 500);
        const worksList = (response.data || []).map((w: any) => ({
          workId: w.workId,
          workCode: w.workCode,
          workName: w.workName,
          workTypeName: w.workTypeName,
          schemeName: w.schemeName,
          zoneName: w.zoneName,
          wardName: w.wardName,
          originalEstimate: w.originalEstimate
        }));
        setWorks(worksList);
      } catch (error) {
        console.error('Error loading works:', error);
      }
    };
    if (isOpen) {
      loadWorks();
    }
  }, [isOpen]);

  // Load work data
  const loadWorkData = useCallback(async (wrkId: number) => {
    try {
      const data = await worksApi.getById(wrkId);
      if (data) {
        setWork({
          workId: data.workId,
          workCode: data.workCode,
          workName: data.workName,
          workTypeName: data.workTypeName,
          schemeName: data.schemeName,
          zoneName: data.zoneName,
          wardName: data.wardName,
          originalEstimate: data.originalEstimate
        });
        // Auto-generate design name from work name if creating new
        if (!isEdit && !designName) {
          setDesignName(`${data.workName} - Design`);
        }
      }
    } catch (error) {
      console.error('Error loading work:', error);
    }
  }, [isEdit, designName]);

  // Load existing design data
  const loadDesignData = useCallback(async () => {
    if (!designId) return;

    setLoading(true);
    try {
      const data = await designsApi.getById(designId);
      if (data) {
        setDesignCode(data.dsgn_cd || '');
        setDesignName(data.dsgn_nm || '');
        setDesignStage(data.dsgn_stge_cd || 'PROPOSED');
        setDesignStatus(data.dsgn_sts_cd || 'DRAFT');
        setEstimateValue(data.estmt_val_at || 0);
        setVersion(data.vrsn_nu || 1);
        setRemarks(data.rmrks_tx || '');
        setSubmittedDate(data.sbmtd_dt ? data.sbmtd_dt.split('T')[0] : '');
        setApprovedDate(data.aprv_dt ? data.aprv_dt.split('T')[0] : '');

        if (data.wrk_id) {
          setSelectedWorkId(data.wrk_id);
          await loadWorkData(data.wrk_id);
        }

        // Load existing drawings/files
        try {
          const drawings = await designsApi.getDrawings(designId);
          if (drawings && drawings.length > 0) {
            const existingFiles: DesignFile[] = drawings.map((d: any) => ({
              id: `existing-${d.drwng_id}`,
              file: null,
              name: d.drwng_nm || d.file_nm,
              type: getFileType(d.file_nm || d.drwng_nm),
              size: d.file_sz || 0,
              url: d.file_url,
              uploaded: true,
              uploading: false,
              description: d.dscn_tx || '',
              drawingId: d.drwng_id,
              incl_dwtn_in: d.incl_dwtn_in || false
            }));
            setFiles(existingFiles);
          }
        } catch (error) {
          console.error('Error loading drawings:', error);
        }
      }
    } catch (error) {
      console.error('Error loading design:', error);
      showError('Failed to load design data');
    } finally {
      setLoading(false);
    }
  }, [designId, loadWorkData]);

  // Load existing designs for the current work (to surface and prevent duplicates)
  const loadExistingDesigns = useCallback(async (wrkId: number | null | undefined) => {
    if (!wrkId) { setExistingDesigns([]); return; }
    try {
      const result = await designsApi.getAll({ workId: wrkId } as any, 1, 50);
      const all = (result.data || []) as ExistingDesign[];
      // Exclude the one currently being edited
      const filtered = designId ? all.filter((d) => d.dsgn_id !== designId) : all;
      setExistingDesigns(filtered);
    } catch (e) {
      console.error('Error loading existing designs for work:', e);
      setExistingDesigns([]);
    }
  }, [designId]);

  // Block creating a duplicate design for a work (allow only when editing)
  const blockDuplicateDesign = !isEdit && existingDesigns.length > 0;

  // Load proposals attached to a work (M:M)
  const loadAttachedProposals = useCallback(async (wrkId: number | null | undefined) => {
    if (!wrkId) { setAttachedProposals([]); return; }
    setLoadingProposals(true);
    try {
      const list = await worksApi.getProposals(wrkId);
      setAttachedProposals(list || []);
    } catch (e) {
      console.error('Error loading attached proposals:', e);
    } finally {
      setLoadingProposals(false);
    }
  }, []);

  const handleAttachProposals = useCallback(async () => {
    if (!selectedWorkId || proposalPickerSelected.size === 0) return;
    try {
      const ids = Array.from(proposalPickerSelected);
      const updated = await worksApi.attachProposals(selectedWorkId, ids);
      setAttachedProposals(updated || []);
      setShowProposalPicker(false);
      setProposalPickerSelected(new Set());
      setProposalPickerSearch('');
      showSuccess(`Attached ${ids.length} proposal(s)`);
    } catch (e) {
      console.error('Attach proposals error:', e);
      showError('Failed to attach proposals');
    }
  }, [selectedWorkId, proposalPickerSelected, showSuccess, showError]);

  const handleDetachProposal = useCallback(async (prpslId: number) => {
    if (!selectedWorkId) return;
    if (!confirm('Detach this proposal from the work?')) return;
    try {
      await worksApi.detachProposal(selectedWorkId, prpslId);
      setAttachedProposals((prev) => prev.filter((p) => p.prpsl_id !== prpslId));
      showSuccess('Proposal detached');
    } catch (e) {
      console.error('Detach proposal error:', e);
      showError('Failed to detach proposal');
    }
  }, [selectedWorkId, showSuccess, showError]);

  // Debounced proposal search for the picker
  useEffect(() => {
    if (!showProposalPicker) return;
    if (proposalPickerSearchRef.current) clearTimeout(proposalPickerSearchRef.current);
    proposalPickerSearchRef.current = setTimeout(async () => {
      setProposalPickerLoading(true);
      try {
        // search goes in listParams: buildFilterParams (the first argument) drops it, so
        // the picker used to show the same first proposals whatever was typed.
        const result = await proposalsApi.getAll(
          {},
          1,
          20, // the WMS list endpoints serve at most 20 rows a page
          proposalPickerSearch ? { search: proposalPickerSearch } : {}
        );
        setProposalPickerResults((result.data || []) as AttachedProposal[]);
      } catch (e) {
        console.error('Proposal search failed:', e);
        setProposalPickerResults([]);
      } finally {
        setProposalPickerLoading(false);
      }
    }, 300);
    return () => { if (proposalPickerSearchRef.current) clearTimeout(proposalPickerSearchRef.current); };
  }, [proposalPickerSearch, showProposalPicker]);

  // Clear transient save state every time the form opens.
  //
  // This became necessary when a clean save started closing the modal: the component
  // is not unmounted (it returns null while closed), so without this the banner and
  // the "already saved" flag from the previous save survive into the next open — a
  // fresh Upload Work Design would appear with the last run's error still on it.
  useEffect(() => {
    if (isOpen) {
      setSaveStatus({ type: null, message: '' });
      setHasSavedSuccessfully(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      if (designId) {
        loadDesignData();
      } else if (workId) {
        setSelectedWorkId(workId);
        loadWorkData(workId);
      }
    }
  }, [isOpen, workId, designId, loadDesignData, loadWorkData]);

  // Reload attached proposals whenever the bound work changes
  useEffect(() => {
    if (isOpen) loadAttachedProposals(selectedWorkId);
  }, [isOpen, selectedWorkId, loadAttachedProposals]);

  // Reload existing designs whenever the bound work changes
  useEffect(() => {
    if (isOpen) loadExistingDesigns(selectedWorkId);
  }, [isOpen, selectedWorkId, loadExistingDesigns]);

  // Resolve the DISPLAY-ONLY Estimated Value for a new design: the work's approved
  // estimate wins, else its most recent one, else the work's own orgnl_estmt_at (the
  // work row is not written back when an estimate is approved, so it can lag). This
  // mirrors what designs.service.js derives at read time, so the form and the grid
  // agree. On edit the value already arrives from getById via loadDesignData, so
  // this bails out on isEdit rather than re-fetching.
  useEffect(() => {
    if (isEdit || !work) return;
    const fallback = Number(work.originalEstimate) || 0;
    const wrkId = work.workId;
    let cancelled = false;

    (async () => {
      let value = 0;
      try {
        const result = await estimatesApi.getAll({ workId: wrkId }, 1, 50);
        const list = (result.data || []) as Array<{ estmt_id: number; totl_at?: number; estmt_sts_cd?: string }>;
        // Approved estimates win; otherwise fall back to the most recent draft estimate.
        const approved = list.filter((e) => e.estmt_sts_cd === 'APPROVED');
        const pick = [...(approved.length ? approved : list)].sort((a, b) => b.estmt_id - a.estmt_id)[0];
        value = Number(pick?.totl_at) || 0;
      } catch (e) {
        console.error('Error loading work estimate for design prefill:', e);
      }
      if (!cancelled) setEstimateValue(value || fallback);
    })();

    return () => { cancelled = true; };
  }, [isEdit, work]);

  // Handle work selection
  const handleSelectWork = (w: Work) => {
    setSelectedWorkId(w.workId);
    setWork(w);
    setShowWorkPicker(false);
    setWorkSearchTerm('');
    if (!designName) {
      setDesignName(`${w.workName} - Design`);
    }
  };

  // Filter works by search
  const filteredWorks = works.filter(w =>
    w.workName?.toLowerCase().includes(workSearchTerm.toLowerCase()) ||
    w.workCode?.toLowerCase().includes(workSearchTerm.toLowerCase())
  );

  // Handle file selection
  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const newFiles: DesignFile[] = Array.from(selectedFiles).map(file => ({
      id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      type: getFileType(file.name),
      size: file.size,
      uploaded: false,
      uploading: false,
      description: ''
    }));

    setFiles(prev => [...prev, ...newFiles]);
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // Remove file - show confirmation first
  const confirmRemoveFile = (fileId: string, fileName: string) => {
    setDeleteConfirm({ show: true, fileId, fileName });
  };

  const removeFile = async () => {
    if (!deleteConfirm.fileId) {
      setDeleteConfirm({ show: false, fileId: null, fileName: '' });
      return;
    }

    const fileToRemove = files.find(f => f.id === deleteConfirm.fileId);

    // If file is uploaded (has drawingId), call API to soft delete
    if (fileToRemove?.uploaded && fileToRemove?.drawingId && designId) {
      try {
        await designsApi.deleteDrawing(designId, fileToRemove.drawingId);
        setFiles(prev => prev.filter(f => f.id !== deleteConfirm.fileId));
        showSuccess('File deleted successfully');
      } catch (error: any) {
        console.error('Error deleting file:', error);
        showError(error.response?.data?.message || 'Failed to delete file');
      }
    } else {
      // Local file (not yet uploaded), just remove from state
      setFiles(prev => prev.filter(f => f.id !== deleteConfirm.fileId));
      showSuccess('File removed');
    }

    setDeleteConfirm({ show: false, fileId: null, fileName: '' });
  };

  const cancelRemoveFile = () => {
    setDeleteConfirm({ show: false, fileId: null, fileName: '' });
  };

  // Update file description
  const updateFileDescription = (fileId: string, description: string) => {
    setFiles(prev => prev.map(f =>
      f.id === fileId ? { ...f, description } : f
    ));
  };

  // Toggle include in Digital Twin
  const toggleIncludeInDigitalTwin = async (file: DesignFile) => {
    console.log('Toggle DT for file:', file.id, 'drawingId:', file.drawingId, 'designId:', designId, 'current:', file.incl_dwtn_in);

    // If file is uploaded, update via API
    if (file.uploaded && file.drawingId && designId) {
      const newValue = !file.incl_dwtn_in;
      console.log('Calling updateDrawing API with incl_dwtn_in:', newValue);
      try {
        const result = await designsApi.updateDrawing(designId, file.drawingId, { incl_dwtn_in: newValue });
        console.log('updateDrawing API result:', result);
        setFiles(prev => prev.map(f =>
          f.id === file.id ? { ...f, incl_dwtn_in: newValue } : f
        ));
        showSuccess(newValue ? 'Added to Digital Twin' : 'Removed from Digital Twin');
      } catch (error: any) {
        console.error('Error updating drawing:', error);
        console.error('Error response:', error.response?.data);
        showError(error.response?.data?.message || 'Failed to update. Check if migration 007 has been run.');
      }
    } else {
      // For local files (not yet uploaded), just update local state
      console.log('File not uploaded yet, updating local state only');
      setFiles(prev => prev.map(f =>
        f.id === file.id ? { ...f, incl_dwtn_in: !f.incl_dwtn_in } : f
      ));
    }
  };

  // Open viewer with uploaded files
  const openViewer = () => {
    const uploadedFiles = files
      .filter(f => f.uploaded && f.url)
      .map(f => ({
        id: f.id,
        name: f.name,
        url: f.url!,
        size: f.size,
        type: f.type as ViewerDesignFile['type'],
        // Pass conversion-related props for CAD files
        drawingId: f.drawingId,
        designId: designId
      }));

    if (uploadedFiles.length === 0) {
      showError('No uploaded files to preview');
      return;
    }

    setViewerFiles(uploadedFiles);
    setShowViewer(true);
  };

  // Preview file(s) - if file has Digital Twin enabled, show all DT-enabled files
  const previewFile = (file: DesignFile) => {
    if (!file.uploaded || !file.url) {
      showError('File must be uploaded before preview');
      return;
    }

    // If file is marked for Digital Twin, show ALL files with incl_dwtn_in = true
    if (file.incl_dwtn_in) {
      const dtFiles = files
        .filter(f => f.uploaded && f.url && f.incl_dwtn_in)
        .map(f => ({
          id: f.id,
          name: f.name,
          url: f.url!,
          size: f.size,
          type: f.type as ViewerDesignFile['type'],
          drawingId: f.drawingId,
          designId: designId
        }));

      if (dtFiles.length === 0) {
        showError('No Digital Twin files to preview');
        return;
      }

      setViewerFiles(dtFiles);
    } else {
      // Show only the clicked file
      setViewerFiles([{
        id: file.id,
        name: file.name,
        url: file.url,
        size: file.size,
        type: file.type as ViewerDesignFile['type'],
        drawingId: file.drawingId,
        designId: designId
      }]);
    }
    setShowViewer(true);
  };

  // Save design
  const handleSave = async () => {
    // Clear previous save status
    setSaveStatus({ type: null, message: '' });

    if (!selectedWorkId) {
      setSaveStatus({ type: 'error', message: 'Please select a work' });
      return;
    }

    if (blockDuplicateDesign) {
      setSaveStatus({ type: 'error', message: 'A design already exists for this work. Close this form and edit the existing design instead.' });
      return;
    }

    if (!designName.trim()) {
      setSaveStatus({ type: 'error', message: 'Please enter design name' });
      return;
    }

    setSaving(true);
    let savedDesignId: number | null = null;
    let saveSucceeded = false;
    const uploadErrors: string[] = [];

    try {
      // estmt_val_at is intentionally NOT sent — it is derived from the work's
      // estimate at read time and has no column on dswms_drwng_lst_t, so sending it
      // would only be silently discarded by the service.
      const payload: Record<string, any> = {
        wrk_id: selectedWorkId,
        dsgn_cd: designCode || undefined,
        dsgn_nm: designName,
        rmrks_tx: remarks || undefined
      };

      // Only include stage/status/dates for new designs (set defaults)
      if (!isEdit) {
        payload.dsgn_stge_cd = 'PROPOSED';
        payload.dsgn_sts_cd = 'DRAFT';
        payload.vrsn_nu = 1;
      }

      if (isEdit && designId) {
        await designsApi.update(designId, payload);
        savedDesignId = designId;
        saveSucceeded = true;
      } else {
        const result = await designsApi.create(payload);
        savedDesignId = result.dsgn_id;
        saveSucceeded = true;
      }

      // Upload new files
      const newFiles = files.filter(f => !f.uploaded && f.file);

      if (newFiles.length > 0 && savedDesignId) {
        for (const fileItem of newFiles) {
          if (fileItem.file) {
            // Mark file as uploading
            setFiles(prev => prev.map(f =>
              f.id === fileItem.id ? { ...f, uploading: true } : f
            ));

            const formData = new FormData();
            formData.append('files', fileItem.file);  // Use 'files' to match global multer middleware
            formData.append('drwng_nm', fileItem.name);
            formData.append('drwng_type_cd', fileItem.type);
            formData.append('dscn_tx', fileItem.description || '');
            formData.append('incl_dwtn_in', fileItem.incl_dwtn_in ? 'true' : 'false');

            try {
              await designsApi.uploadDrawing(savedDesignId, formData);
              // Mark file as uploaded
              setFiles(prev => prev.map(f =>
                f.id === fileItem.id ? { ...f, uploading: false, uploaded: true } : f
              ));
            } catch (uploadError: any) {
              console.error('Error uploading file:', fileItem.name, uploadError);
              uploadErrors.push(fileItem.name);
              // Mark file as failed
              setFiles(prev => prev.map(f =>
                f.id === fileItem.id ? { ...f, uploading: false, error: 'Upload failed' } : f
              ));
            }
          }
        }
      }

      // A clean save closes the form; only a partial failure keeps it open.
      //
      // The success banner used to be rendered inside the modal and the operator had
      // to dismiss it by hand — so "Design created successfully" sat under a Save
      // button that was still armed, and pressing it again hit the duplicate-design
      // guard. The confirmation now goes to the toast, where the rest of the module
      // puts it, and the modal gets out of the way.
      if (saveSucceeded) {
        if (uploadErrors.length > 0) {
          // Files failed: STAY OPEN. The failed rows are marked in the file list and
          // closing here would discard the only place that says which ones. The design
          // itself is saved, so the parent still refreshes whenever the form is closed.
          setHasSavedSuccessfully(true);
          setSaveStatus({
            type: 'error',
            message: `Design saved but ${uploadErrors.length} file(s) failed to upload: ${uploadErrors.join(', ')}`
          });
        } else {
          showSuccess(
            newFiles.length > 0
              ? `${isEdit ? 'Design updated' : 'Design created'} and ${newFiles.length} file(s) uploaded successfully`
              : (isEdit ? 'Design updated successfully' : 'Design created successfully')
          );
          // Called directly rather than through handleClose(): setHasSavedSuccessfully
          // has not committed yet in this tick, so handleClose() would read the stale
          // false and skip the parent refresh.
          onSuccess();
          onClose();
          return;
        }
      }
    } catch (error: any) {
      console.error('Error saving design:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save design';
      setSaveStatus({ type: 'error', message: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Group files by type
  const filesByType = files.reduce((acc, file) => {
    if (!acc[file.type]) acc[file.type] = [];
    acc[file.type].push(file);
    return acc;
  }, {} as Record<string, DesignFile[]>);

  return createPortal(
    <div className="fixed inset-0 overflow-y-auto z-[9999]">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={handleClose}></div>

      {/* Modal */}
      <div className={`flex min-h-full items-center justify-center ${isFullscreen ? 'p-0' : 'p-4'}`}>
        <div className={`relative bg-white shadow-xl transform transition-all ${
          isFullscreen
            ? 'w-full h-screen rounded-none'
            : 'w-full max-w-5xl rounded-xl'
        }`}>
          {/* Header */}
          <div className={`${WMS_POPUP_HEADER} ${
            isFullscreen ? '' : 'rounded-t-xl'
          }`}>
            <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <div className={WMS_POPUP_ICON}>
                  <i className="ri-draft-line"></i>
                </div>
                <div className="min-w-0">
                  <h2 className={WMS_POPUP_TITLE}>
                    {isEdit ? 'Edit Design' : 'Create Work Design'}
                  </h2>
                  <p className={WMS_POPUP_SUBTITLE}>
                    {work ? `${work.workCode} - ${work.workName}` : 'Upload BIM, CAD, PDF and supporting documents'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className={WMS_POPUP_CLOSE}
                  title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-base`}></i>
                </button>
                <button
                  onClick={handleClose}
                  className={WMS_POPUP_CLOSE}
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className={`overflow-y-auto p-6 bg-gray-50 ${isFullscreen ? 'h-[calc(100vh-130px)]' : 'max-h-[70vh]'}`}>
            {loading ? (
              <TableSkeleton />
            ) : (
              <div className="space-y-6">
                {/* Work Selection */}
                <div className="bg-white rounded-lg border p-4">
                  <SectionHeader icon="ri-building-line" title="Work Information (Add design to existing work)" />

                  <WorkSelector
                    selectedWork={work ? {
                      wrk_id: work.workId, wrk_cd: work.workCode, wrk_nm: work.workName,
                      wrk_type_nm: work.workTypeName, ulb_nm: work.zoneName, orgnl_estmt_at: work.originalEstimate
                    } : null}
                    onSelect={(sw: SelectedWork) => {
                      handleSelectWork({
                        workId: sw.wrk_id, workCode: sw.wrk_cd, workName: sw.wrk_nm,
                        workTypeName: sw.wrk_type_nm, schemeName: '', zoneName: sw.ulb_nm,
                        wardName: '', originalEstimate: sw.orgnl_estmt_at
                      });
                    }}
                    onClear={() => { setWork(null); setSelectedWorkId(null); }}
                    label=""
                    placeholder="Search works without a design yet..."
                    disabled={isEdit}
                    // Only surface works that currently have no active design.
                    // (In edit mode the work is already chosen and the selector is disabled.)
                    extraFilters={isEdit ? undefined : { withoutDesign: 'true' }}
                    // withoutDesign can empty this list entirely — in a UDA whose works
                    // all have designs there is genuinely nothing to create against.
                    // Say so, rather than inviting the user to keep typing.
                    emptyFilteredMessage="Every work in this UDA already has a design. To change one, open it from the Designs list and use Edit."
                  />
                </div>

                {/* Attached Proposals */}
                <div className="bg-purple-50/40 p-4 rounded-lg border border-purple-200">
                  <SectionHeader
                    icon="ri-stack-line"
                    title="Attached Proposals"
                    action={
                      <button
                        type="button"
                        onClick={() => { setProposalPickerSelected(new Set()); setProposalPickerSearch(''); setShowProposalPicker(true); }}
                        disabled={!selectedWorkId}
                        title={!selectedWorkId ? 'Select a work first to attach proposals' : 'Attach a proposal'}
                        className="px-2 py-1 bg-white/20 hover:bg-white/30 text-white text-xs rounded-md flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i className="ri-add-line"></i> Attach Proposal
                      </button>
                    }
                  />
                  {loadingProposals ? (
                    <ListSkeleton rows={2} avatar={false} />
                  ) : attachedProposals.length === 0 ? (
                    <div className="text-sm text-gray-500 italic px-4 py-3 bg-white rounded border border-dashed text-center">
                      No proposal attached
                      {selectedWorkId && (
                        <span className="block text-xs text-gray-400 mt-1">Use "Attach Proposal" to link one or more proposals to this work.</span>
                      )}
                    </div>
                  ) : (
                    <ul className="bg-white rounded-lg border divide-y">
                      {attachedProposals.map((p) => (
                        <li key={p.prpsl_id} className="flex items-center justify-between gap-3 px-3 py-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{p.prpsl_cd}</span>
                              <span className="text-sm font-medium text-gray-900 truncate">{p.prpsl_nm}</span>
                              {p.prpsl_sts_cd && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{p.prpsl_sts_cd}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                              <span>Estimated: <span className="font-medium text-emerald-700">₹{(p.estmt_cst_at || 0).toLocaleString('en-IN')}</span></span>
                              {p.lctn_tx && <span className="truncate">{p.lctn_tx}</span>}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDetachProposal(p.prpsl_id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Detach proposal"
                          >
                            <i className="ri-close-circle-line text-base"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Existing Designs on This Work — duplicate guard */}
                {!isEdit && existingDesigns.length > 0 && (
                  <div className="p-4 rounded-lg border bg-red-50 border-red-300">
                    <div className="flex items-start gap-3 mb-3">
                      <i className="ri-error-warning-line text-xl text-red-600 mt-0.5"></i>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-red-800">
                          A design already exists for this work
                        </h4>
                        <p className="text-xs mt-0.5 text-red-700">
                          Close this form and edit the existing design entry below instead of creating a duplicate.
                        </p>
                      </div>
                    </div>
                    <ul className="bg-white rounded-lg border divide-y">
                      {existingDesigns.map((d) => (
                        <li key={d.dsgn_id} className="flex items-center justify-between gap-3 px-3 py-2">
                          <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">{d.dsgn_cd}</span>
                            {d.dsgn_stge_nm && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{d.dsgn_stge_nm}</span>
                            )}
                            {d.vrsn_nu && d.vrsn_nu > 1 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">Rev {d.vrsn_nu}</span>
                            )}
                            <span className="text-sm text-gray-800 truncate">{d.dsgn_nm}</span>
                            <span className="text-xs text-gray-500 ml-auto">₹{(d.estmt_val_at || 0).toLocaleString('en-IN')}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Design Details */}
                <div className="bg-white rounded-lg border p-4">
                  <SectionHeader icon="ri-draft-line" title="Design Details" />
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Design Code
                      </label>
                      <input
                        type="text"
                        value={designCode}
                        onChange={(e) => setDesignCode(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                        placeholder="Auto-generated if empty"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Design Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={designName}
                        onChange={(e) => setDesignName(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                        placeholder="Enter design name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Design Stage
                      </label>
                      <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-gray-600">
                        {DESIGN_STAGES.find(s => s.code === designStage)?.name || designStage}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-gray-600">
                        {designStatus === 'DRAFT' ? 'Draft' :
                         designStatus === 'ACTIVE' ? 'Active' :
                         designStatus === 'SUBMITTED' ? 'Submitted' :
                         designStatus === 'APPROVED' ? 'Approved' :
                         designStatus === 'REJECTED' ? 'Rejected' : designStatus}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Version
                      </label>
                      <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-gray-600">
                        {version}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Estimated Value
                      </label>
                      {/* Derived, never stored — dswms_drwng_lst_t has no estmt_val_at
                          column and the source model does not persist one. Shown
                          read-only so it cannot accept a value the backend discards. */}
                      <div
                        className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-gray-600"
                        title="Derived from the work's approved estimate (falls back to the work's original estimate)"
                      >
                        {estimateValue
                          ? `₹${estimateValue.toLocaleString('en-IN')}`
                          : '-'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Submitted Date
                      </label>
                      <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-gray-600">
                        {submittedDate || '-'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Approved Date
                      </label>
                      <div className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-gray-600">
                        {approvedDate || '-'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* File Upload */}
                <div className="bg-white rounded-lg border p-4">
                  <SectionHeader
                    icon="ri-upload-cloud-2-line"
                    title="Design Files"
                    action={
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-white/80">
                          {files.length} file(s)
                        </span>
                        {files.some(f => f.uploaded && f.url) && (
                          <button
                            onClick={openViewer}
                            className="text-xs px-2 py-1 bg-white/20 hover:bg-white/30 rounded flex items-center gap-1 text-white"
                          >
                            <i className="ri-eye-line"></i>
                            View All
                          </button>
                        )}
                      </div>
                    }
                  />

                  {/* Dropzone */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                      isDragging
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={ALL_ACCEPTED_EXTENSIONS}
                      onChange={(e) => handleFileSelect(e.target.files)}
                      className="hidden"
                    />
                    <i className={`ri-upload-cloud-2-line text-4xl mb-3 block ${
                      isDragging ? 'text-indigo-500' : 'text-gray-400'
                    }`}></i>
                    <p className="text-gray-600 mb-2">
                      <span className="font-medium text-indigo-600">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-sm text-gray-500">
                      BIM (.ifc, .rvt) | CAD (.dwg, .dxf) | GIS (.kml, .shp) | PDF | Images | Documents
                    </p>
                  </div>

                  {/* File type shortcuts */}
                  <div className="flex gap-2 mt-4 flex-wrap">
                    {Object.entries(FILE_TYPES).map(([type, config]) => (
                      <button
                        key={type}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.multiple = true;
                          input.accept = config.accept;
                          input.onchange = (e) => handleFileSelect((e.target as HTMLInputElement).files);
                          input.click();
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 transition-colors
                          ${type === 'BIM' ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' :
                            type === 'CAD' ? 'bg-purple-50 text-purple-700 hover:bg-purple-100' :
                            type === 'GIS' ? 'bg-teal-50 text-teal-700 hover:bg-teal-100' :
                            type === 'PDF' ? 'bg-red-50 text-red-700 hover:bg-red-100' :
                            type === 'IMAGE' ? 'bg-green-50 text-green-700 hover:bg-green-100' :
                            'bg-gray-50 text-gray-700 hover:bg-gray-100'
                          }`}
                      >
                        <i className={config.icon}></i>
                        {config.label}
                      </button>
                    ))}
                  </div>

                  {/* Files Table */}
                  {files.length > 0 && (
                    <div className="mt-6 border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-3 text-left font-medium text-gray-600 w-8">#</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">File Name</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600 w-24">Type</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600 w-24">Size</th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600 w-24">Status</th>
                            <th className="px-4 py-3 text-center font-medium text-gray-600 w-20" title="Include in Digital Twin Viewer">
                              <span className="flex items-center justify-center gap-1">
                                <i className="ri-3d-cube-sphere-line"></i>
                                DT
                              </span>
                            </th>
                            <th className="px-4 py-3 text-left font-medium text-gray-600">Description</th>
                            <th className="px-4 py-3 text-center font-medium text-gray-600 w-28">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {files.map((file, index) => {
                            const config = FILE_TYPES[file.type as keyof typeof FILE_TYPES];
                            return (
                              <tr key={file.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <i className={`${config.icon} ${
                                      file.type === 'BIM' ? 'text-blue-600' :
                                      file.type === 'CAD' ? 'text-purple-600' :
                                      file.type === 'GIS' ? 'text-teal-600' :
                                      file.type === 'PDF' ? 'text-red-600' :
                                      file.type === 'IMAGE' ? 'text-green-600' :
                                      'text-gray-600'
                                    }`}></i>
                                    <span className="font-medium truncate max-w-[250px]" title={file.name}>
                                      {file.name}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    file.type === 'BIM' ? 'bg-blue-100 text-blue-700' :
                                    file.type === 'CAD' ? 'bg-purple-100 text-purple-700' :
                                    file.type === 'GIS' ? 'bg-teal-100 text-teal-700' :
                                    file.type === 'PDF' ? 'bg-red-100 text-red-700' :
                                    file.type === 'IMAGE' ? 'bg-green-100 text-green-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {file.type}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-gray-600">
                                  {formatFileSize(file.size)}
                                </td>
                                <td className="px-4 py-3">
                                  {file.uploaded ? (
                                    <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full flex items-center gap-1 w-fit">
                                      <i className="ri-check-line"></i>
                                      Uploaded
                                    </span>
                                  ) : file.uploading ? (
                                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full flex items-center gap-1 w-fit">
                                      <i className="ri-loader-4-line animate-spin"></i>
                                      Uploading
                                    </span>
                                  ) : file.error ? (
                                    <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full flex items-center gap-1 w-fit" title={file.error}>
                                      <i className="ri-error-warning-line"></i>
                                      Failed
                                    </span>
                                  ) : (
                                    <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full flex items-center gap-1 w-fit">
                                      <i className="ri-time-line"></i>
                                      Pending
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button
                                    onClick={() => toggleIncludeInDigitalTwin(file)}
                                    className="flex items-center justify-center gap-1"
                                    title={file.incl_dwtn_in ? 'Remove from Digital Twin' : 'Add to Digital Twin'}
                                  >
                                    <div
                                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                        file.incl_dwtn_in
                                          ? 'bg-indigo-600 border-indigo-600'
                                          : 'bg-white border-gray-300 hover:border-indigo-400'
                                      }`}
                                    >
                                      {file.incl_dwtn_in && (
                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </div>
                                  </button>
                                </td>
                                <td className="px-4 py-3">
                                  <input
                                    type="text"
                                    value={file.description}
                                    onChange={(e) => updateFileDescription(file.id, e.target.value)}
                                    placeholder="Add description..."
                                    className="w-full text-sm border border-gray-200 rounded px-2 py-1 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center justify-center gap-1">
                                    {file.uploaded && file.url && (
                                      <button
                                        onClick={() => previewFile(file)}
                                        className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                                        title="Preview"
                                      >
                                        <i className="ri-eye-line"></i>
                                      </button>
                                    )}
                                    {file.url && (
                                      <a
                                        href={file.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                                        title="Download"
                                      >
                                        <i className="ri-download-line"></i>
                                      </a>
                                    )}
                                    <button
                                      onClick={() => confirmRemoveFile(file.id, file.name)}
                                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                                      title="Remove"
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {/* Summary row */}
                      <div className="bg-gray-50 px-4 py-2 border-t flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                          Total: <strong>{files.length}</strong> file(s)
                        </span>
                        <div className="flex items-center gap-4 text-gray-500">
                          <span className="text-green-600">{files.filter(f => f.uploaded).length} uploaded</span>
                          <span className="text-amber-600">{files.filter(f => !f.uploaded && !f.error).length} pending</span>
                          {files.filter(f => f.error).length > 0 && (
                            <span className="text-red-600">{files.filter(f => f.error).length} failed</span>
                          )}
                          {files.filter(f => f.incl_dwtn_in).length > 0 && (
                            <span className="text-indigo-600 flex items-center gap-1">
                              <i className="ri-3d-cube-sphere-line"></i>
                              {files.filter(f => f.incl_dwtn_in).length} in Digital Twin
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Remarks */}
                <div className="bg-white rounded-lg border p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Remarks / Notes
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                    placeholder="Any additional remarks about the design..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={`border-t bg-gray-50 ${
            isFullscreen ? '' : 'rounded-b-xl'
          }`}>
            {/* Save Status Message */}
            {saveStatus.type && (
              <div className={`px-6 py-3 flex items-center gap-3 ${
                saveStatus.type === 'success'
                  ? 'bg-green-50 border-b border-green-200'
                  : 'bg-red-50 border-b border-red-200'
              }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  saveStatus.type === 'success' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  <i className={`text-lg ${
                    saveStatus.type === 'success'
                      ? 'ri-check-line text-green-600'
                      : 'ri-error-warning-line text-red-600'
                  }`}></i>
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${
                    saveStatus.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {saveStatus.type === 'success' ? 'Success' : 'Error'}
                  </p>
                  <p className={`text-sm ${
                    saveStatus.type === 'success' ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {saveStatus.message}
                  </p>
                </div>
                <button
                  onClick={() => setSaveStatus({ type: null, message: '' })}
                  className={`p-1 rounded hover:bg-opacity-20 ${
                    saveStatus.type === 'success'
                      ? 'text-green-600 hover:bg-green-600'
                      : 'text-red-600 hover:bg-red-600'
                  }`}
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            )}

            {/* Action Buttons */}
            <div className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Close
                </button>
                <div className="text-sm text-gray-500">
                  {files.filter(f => !f.uploaded).length > 0 && (
                    <span>{files.filter(f => !f.uploaded).length} new file(s) to upload</span>
                  )}
                </div>
              </div>
              <button
                onClick={handleSave}
                disabled={saving || !selectedWorkId || !designName.trim() || blockDuplicateDesign}
                title={blockDuplicateDesign ? 'A design already exists for this work — close this form and edit the existing design instead.' : undefined}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line"></i>
                    {isEdit ? 'Update Design' : 'Save Design'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={cancelRemoveFile}></div>
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <i className="ri-delete-bin-line text-red-600 text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete File</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <strong>"{deleteConfirm.fileName}"</strong>?
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={cancelRemoveFile}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={removeFile}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Digital Twin Viewer Modal */}
      {showViewer && (
        <div className="fixed inset-0 z-[10000] bg-black">
          <div className="h-full flex flex-col">
            {/* Viewer Header */}
            <div className={WMS_POPUP_HEADER}>
              <div className="flex items-center gap-2 min-w-0">
                <div className={WMS_POPUP_ICON}>
                  <i className="ri-eye-line"></i>
                </div>
                <div className="min-w-0">
                  <h3 className={WMS_POPUP_TITLE}>Digital Twin Viewer</h3>
                  <p className={WMS_POPUP_SUBTITLE}>
                    {designName || 'Design Preview'} - {viewerFiles.length} file(s)
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowViewer(false)}
                className={WMS_POPUP_CLOSE}
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Viewer Content */}
            <div className="flex-1 overflow-hidden">
              <DigitalTwinViewer
                files={viewerFiles}
                title={designName || 'Design Preview'}
                height="100%"
              />
            </div>
          </div>
        </div>
      )}

      {/* Proposal Attach Picker */}
      {showProposalPicker && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowProposalPicker(false)}></div>
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl">
              <div className={`${WMS_POPUP_HEADER} rounded-t-xl`}>
                <div className="flex items-center gap-2 min-w-0">
                  <i className="ri-stack-line text-base text-white/90"></i>
                  <div className="min-w-0">
                    <h2 className={WMS_POPUP_TITLE}>Attach Proposals</h2>
                    <p className={WMS_POPUP_SUBTITLE}>Select one or more proposals to link to this work</p>
                  </div>
                </div>
                <button onClick={() => setShowProposalPicker(false)} className={WMS_POPUP_CLOSE}>
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="px-6 py-3 border-b">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    value={proposalPickerSearch}
                    onChange={(e) => setProposalPickerSearch(e.target.value)}
                    placeholder="Search proposals by code or name..."
                    className="w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  {proposalPickerResults.length} result{proposalPickerResults.length !== 1 ? 's' : ''}
                  {proposalPickerSelected.size > 0 && (
                    <span className="ml-2 text-purple-700 font-medium">· {proposalPickerSelected.size} selected</span>
                  )}
                </p>
              </div>
              <div className="max-h-[50vh] overflow-y-auto">
                {proposalPickerLoading ? (
                  <ListSkeleton />
                ) : proposalPickerResults.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <i className="ri-inbox-line text-4xl mb-2 block opacity-30"></i>
                    <p className="font-medium">No proposals found</p>
                  </div>
                ) : (
                  <ul className="p-4 space-y-2">
                    {proposalPickerResults.map((p) => {
                      const alreadyAttached = attachedProposals.some((a) => a.prpsl_id === p.prpsl_id);
                      const checked = proposalPickerSelected.has(p.prpsl_id);
                      return (
                        <li key={p.prpsl_id}>
                          <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-all ${alreadyAttached ? 'opacity-60 bg-gray-50' : checked ? 'border-purple-500 bg-purple-50' : 'hover:border-purple-300'}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={alreadyAttached}
                              onChange={(e) => {
                                setProposalPickerSelected((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(p.prpsl_id); else next.delete(p.prpsl_id);
                                  return next;
                                });
                              }}
                              className="mt-1 accent-purple-600"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-sm text-purple-700 bg-purple-100 px-2 py-0.5 rounded">{p.prpsl_cd}</span>
                                {alreadyAttached && <span className="text-xs text-gray-500 italic">already attached</span>}
                              </div>
                              <p className="font-medium text-gray-900 truncate">{p.prpsl_nm}</p>
                              <p className="text-sm text-gray-500">
                                Estimated: <span className="font-medium text-emerald-700">₹{(p.estmt_cst_at || 0).toLocaleString('en-IN')}</span>
                              </p>
                            </div>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="px-6 py-3 border-t flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowProposalPicker(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >Cancel</button>
                <button
                  type="button"
                  onClick={handleAttachProposals}
                  disabled={proposalPickerSelected.size === 0}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Attach {proposalPickerSelected.size > 0 ? `(${proposalPickerSelected.size})` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
